import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '../../firebase';
import { FiBell, FiCheckCircle, FiAlertCircle, FiTag } from 'react-icons/fi';
import { FaRocket } from 'react-icons/fa';
import { slugify } from '../../utils/helpers';
import './Notifications.css';

const NotificationsPage = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentUser) {
      navigate('/');
      return;
    }

    const fetchNotifications = async () => {
      try {
        const q = query(
          collection(db, 'notifications'),
          where('userId', '==', currentUser.uid),
          orderBy('createdAt', 'desc'),
          limit(20)
        );
        const snapshot = await getDocs(q);
        const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setNotifications(data);
      } catch (err) {
        // Log silently, empty state will show
        console.log("No notifications found or error fetching:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchNotifications();
  }, [currentUser, navigate]);

  const handleNotificationClick = (notif) => {
    // If notification has a specific link, navigate to it
    if (notif.link) {
      navigate(notif.link);
      return;
    }

    // Fallback: If it has an adId, navigate to the ad
    if (notif.adId) {
      const adTitleSlug = notif.adTitle ? slugify(notif.adTitle) : 'ad';
      navigate(`/item/${notif.adId}/${adTitleSlug}`);
    }
  };

  const getIconForType = (type) => {
    switch (type) {
      case 'ad_posted':
        return <FiCheckCircle />;
      case 'ad_expired':
      case 'ad_rejected':
        return <FiAlertCircle />;
      case 'ad_boosted':
        return <FaRocket />;
      case 'ad_sold':
        return <FiTag />;
      default:
        return <FiBell />;
    }
  };

  const getIconClassForType = (type) => {
    switch (type) {
      case 'ad_posted':
        return 'success';
      case 'ad_expired':
      case 'ad_rejected':
        return 'error';
      case 'ad_boosted':
        return 'boost';
      case 'ad_sold':
        return 'sold';
      default:
        return '';
    }
  };

  return (
    <div className="notifications-page">
      <div className="container">
        <div className="dashboard-header" style={{ marginBottom: '40px' }}>
          <div className="dashboard-heading">
            <h1>Notifications</h1>
            <p>Stay updated with your latest activity</p>
          </div>
        </div>

        <div className="notifications-content">
          {loading ? (
            <div className="loader-full">Loading...</div>
          ) : notifications.length > 0 ? (
            <div className="notifications-list">
              {notifications.map(notif => (
                <div
                  key={notif.id}
                  className={`notification-card ${!notif.read ? 'unread' : ''}`}
                  onClick={() => handleNotificationClick(notif)}
                >
                  <div className={`notification-icon ${getIconClassForType(notif.type)}`}>
                    {getIconForType(notif.type)}
                  </div>
                  <div className="notification-details">
                    <h4>{notif.title || 'New Notification'}</h4>
                    <p>{notif.message}</p>
                    <span className="notification-time">
                      {notif.createdAt?.toDate ? notif.createdAt.toDate().toLocaleDateString() : 'Just now'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-notifications">
              <div className="empty-icon-wrapper">
                <FiBell className="empty-icon" />
              </div>
              <h3>No Notifications Yet</h3>
              <p>When you get updates, they'll show up here.</p>
              <button className="btn btn-primary-glossy" onClick={() => navigate('/explore')}>
                Explore Marketplace
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationsPage;
